import { CategoryModel } from '../services/content/category/models/CategoryModel';
import { getNavbarCategories } from '../services/configurator/navbar/NavbarConfigurator';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { NavigationMenuContext } from '../contexts/NavigationMenuContext';
import Layout from '../components/Layout/Layout';
import styles from '../styles/PrivacyPolicy.module.scss';
import Head from 'next/head';

type Props = {
    categories: CategoryModel[];
};

export async function getStaticProps() {
    const categories = await getNavbarCategories();

    return {
        props: {
            categories: categories,
        },
        revalidate: 10,
    };
}

enum TabsEnum {
    PRIVACY_POLICY = 'privacy_policy',
    COOKIES_POLICY = 'cookies_policy',
    GDPR = 'gdpr',
}

const PrivacyPolicy: FunctionComponent<Props> = ({ categories }) => {
    const [tab, setTab] = useState(TabsEnum.PRIVACY_POLICY);

    useEffect(() => {
        const tabHashValue = window.location.hash.substring(1);

        if (
            tabHashValue &&
            (tabHashValue.toLowerCase() === TabsEnum.GDPR ||
                tabHashValue.toLowerCase() === TabsEnum.PRIVACY_POLICY ||
                tabHashValue.toLowerCase() === TabsEnum.COOKIES_POLICY)
        ) {
            setTab(tabHashValue as TabsEnum);
        }
    }, []);

    return (
        <NavigationMenuContext.Provider value={categories}>
            <Layout>
                <Head>
                    <link rel='canonical' href={`${process.env.NEXT_PUBLIC_WEBSITE_URL}/privacy-policy`} />
                </Head>
                <div className={`container`}>
                    <div className={`row ${styles.mainSection}`}>
                        <div className='col'>
                            <h1 style={{ fontSize: '28px', textTransform: 'uppercase' }}>Вашите лични данни</h1>
                            <p>
                                Както всички други сайтове и мобилни апликации, Ние събираме и обработваме Вашите лични
                                данни. За гарантиране на правата Ви по GDPR, Спортал моли за Вашето съгласие.
                            </p>
                            <p>Моля да прегледате внимателно за какви цели събираме Вашите лични данни.</p>
                            <p>
                                Ако натиснете бутона Приемам всички, ще дадете съгласието си с нас и на всички трети
                                страни, описани детайлно в Политиката за поверителност и Политиката за бисквитките .
                            </p>
                            <p>
                                Ако искате да получите повече информация и да направите специфични настройки само за
                                конкретни цели, използвайте бутона Настройки за поверителност.
                            </p>
                        </div>
                    </div>
                    <div className={`row ${styles.tabsContainer}`}>
                        <div
                            role='button'
                            className={`col-12 col-md-4 col-lg-4 text-center pt-3 pb-3 ${styles.tab} ${
                                tab === TabsEnum.PRIVACY_POLICY ? styles.tabActive : ''
                            }`}
                            onClick={() => setTab(TabsEnum.PRIVACY_POLICY)}>
                            Политика за поверителност
                        </div>
                        <div
                            role='button'
                            className={`col-12 col-md-4 col-lg-4 text-center pt-3 pb-3 ${styles.tab} ${
                                tab === TabsEnum.COOKIES_POLICY ? styles.tabActive : ''
                            }`}
                            onClick={() => setTab(TabsEnum.COOKIES_POLICY)}>
                            Политика за бисквитки
                        </div>
                        <div
                            role='button'
                            className={`col-12 col-md-4 col-lg-4 text-center pt-3 pb-3 ${styles.tab} ${
                                tab === TabsEnum.GDPR ? styles.tabActive : ''
                            }`}
                            onClick={() => setTab(TabsEnum.GDPR)}>
                            Права по GDPR
                        </div>
                    </div>
                    <main className={`row ${styles.body}`}>
                        <div className='col'>
                            <Tabs tab={tab} />
                        </div>
                    </main>
                </div>
            </Layout>
        </NavigationMenuContext.Provider>
    );
};

export default PrivacyPolicy;

const Tabs: FunctionComponent<{ tab: TabsEnum }> = ({ tab }) => {
    if (tab === TabsEnum.PRIVACY_POLICY) {
        return <PrivacyPolicyBody />;
    }

    if (tab === TabsEnum.COOKIES_POLICY) {
        return <CookiesPolicyBody />;
    }

    if (tab === TabsEnum.GDPR) {
        return <GDPRBody />;
    }

    return null;
};

const GDPRBody: FunctionComponent = () => {
    return (
        <div className='pt-3'>
            <h2 className={`${styles.title}`}>ПРАВА НА ПОТРЕБИТЕЛИТЕ ПО ОБЩИЯ РЕГЛАМЕНТ ЗА ЗАЩИТА НА ЛИЧНИТЕ ДАННИ</h2>

            <span className={styles.subtitle}>Общи положения</span>
            <p className='spd-text'>
                Съгласно Общия регламент за защита на личните данни (РЕГЛАМЕНТ (ЕС) 2016/679 НА ЕВРОПЕЙСКИЯ ПАРЛАМЕНТ И
                НА СЪВЕТА от 27 април 2016 година - GDPR), Вие, като субект на събиране и обработка на лични данни,
                имате право да упражните посочените по-долу Ваши права.
            </p>
            <p className='spd-text'>Преди да имате тази възможност, е необходимо да Ви идентифицираме.</p>
            <p className='spd-text'>
                Идентификацията Ви ще стане чрез логин в услугата, за която искате да упражните правата си. Ако сте
                използвали услуга, за която няма създаден профил, ще извършим идентификацията Ви на място в нашия офис.
            </p>
            <p className='spd-text'>
                Определният в Регламента срок за отговор на Вашето искане е до 1 (един) месец. С оглед сложността и броя
                на исканията, можем да удължим този срок с още 2 (два) месеца, за което ще Ви уведомим по избран от Вас
                подходящ начин.
            </p>

            <p className='spd-text'>
                Имате избор как да подадете искането си – може да го направите в електронен вид от нашия Център за
                поверителност или в хартиен вид – от нашия търговски офис. Тъй като не поддържаме единна база с данни от
                всичките ни сайтове/мобилни апликации/продукти/услуги, ще трябва да посочите конкретен сайт/мобилна
                апликации/продукт или услуга, за който искате да упражните правата си.
            </p>
            <p className='spd-text'>
                Ще Ви уведомим по посочения от Вас начин за резултата от всяко Ваше искане по реда на GDPR.
            </p>

            <span className={styles.subtitle}>Списък с правата Ви</span>
            <div className='spd-sublist'>
                <p>
                    Право във всеки един момент да оттеглите съгласието си за обработка на личните Ви данни съгласно
                    изискванията на Регламента;
                </p>
                <p>
                    Право на коригиране на личните данни - ако смятате, че има грешка в събраните за Вас лични данни,
                    може да поискате тяхната корекция. Ще трябва да ни посочите верните според Вас данни.
                </p>
                <p>
                    Право на достъп до личните Ви данни - имате право да получите потвърждение дали обработваме Вашите
                    лични данни и ако е така, да получите достъп до тях. Заедно с данните ще Ви дадем достъп и до
                    задължителната информация, описана в чл. 15, т. 1 от Регламента.
                </p>
                <p>
                    Право на изтриване на личните Ви данни - може да поискате да бъдете забравен, като ние ще преценим
                    дали са изпълнени изискванията на чл. 17 от Регламента.
                </p>
                <p>
                    Право на ограничаване на обработването - означава маркиране на съхранявани лични данни с цел
                    ограничаване на обработването им в бъдеще. Ако искате да запазим Вашите лични данни, без да ги
                    обработваме, може да пуснете искане по тази точка.
                </p>
                <p>
                    Право на преносимост на данните - може да поискате събраните от нас лични данни за Вас да бъдат
                    предадени в структуриран, широко използван и пригоден за машинно четене вид. Към момента нямаме
                    възможност пряко да прехвърлим данните Ви на друг администратор.
                </p>
                <p>
                    Право на възражение – по всяко време може да възразите срещу обработката на лични Ви данни,
                    включително профилиране и директен маркетинг.
                </p>
                <p>
                    Право да поискате от Администратора на личните Ви данни да уведомите третите лица, на които
                    Администраторът е предоставил Вашите данни, по отношение на коригиране, изтриване или ограничаване
                    на обработването на личните данни. Във всички случаи, когато упражните някое от горепосочените
                    права, ние ще уведомим тези трети лица за Вашето искане.
                </p>
                <p>
                    Имате право на жалба до Комисия за защита на личните данни, която се подава във форма и с реквизити
                    съгласно установеното в закона.
                </p>
            </div>
        </div>
    );
};

const CookiesPolicyBody: FunctionComponent = () => {
    return (
        <div className='pt-3'>
            <h2 className={`${styles.title}`}>ПОЛИТИКА ЗА БИСКВИТКИ (COOKIES) на СПОРТАЛ</h2>
            <span className={styles.subtitle}>Какво включва тази политика?</span>
            <p className='spd-text'>Уебсайтове на Спортал Медия Груп (Спортал) използват бисквитки (cookies).</p>
            <p className='spd-text'>
                За да научите повече за това какво представляват бисквитките, как се използват на нашите уебсайтове и
                това как да ги управлявате, прочетете внимателно настоящата Политика за Бисквитки на Спортал.
            </p>

            <span className={styles.subtitle}>Какво представляват бисквитките?</span>
            <p className='spd-text'>
                Бисквитката представлява малък файл, съдържащ данни, който се съхранява в уеб браузъра на Вашето
                устройство при посещаване на конкретни уебсайтове. Бисквитките могат да съдържат, но могат и да не
                съдържат лични данни за посетителя.
            </p>

            <span className='spd-subtitle'>Защо се използват бисквитките?</span>
            <p className='spd-text'>За да работи правилно, всеки съвременен уебсайт използва бисквитки.</p>
            <p className='spd-text'>
                Те имат различни задачи, които най-общо улесняват и правят по-интерактивно Вашето ползване на
                уебсайтовете ни.Например, чрез бисквитките събираме информация във връзка с Вашето посещение и поведение
                на сайта, така че не трябва да ни я предоставяте отново, и отново. По този начин, можем да Ви разпознаем
                при следващо посещение на уебсайтовете ни и да Ви покажем.
            </p>
            <p className='spd-text'>
                Бисквитките ни помагат и да разберем как използвате нашите услуги, за да можем непрекъснато да ги
                подобряваме, чрез персонализиране на съдържанието и предложенията към Вас, на база Вашите предпочитания
                и интереси.
            </p>
            <p className='spd-text'>
                Нашите уебсайтове са безплатни за Вас. За да можем да осигурим средства за нормалната им работа, ние Ви
                сервираме платена реклама. Използваме бисквитките, за да запазим информация свързана с това колко често
                и на какви места сте виждали реклами, каква реклама Ви е била показвана, по кое време и на кой сайт и
                Вашето поведение спрямо рекламите – дали сте кликнали на реклама. Тази информация ни помага да Ви
                показваме все по-релевантни спрямо Вашите интереси и Вашия профил реклами. Ако нямаме възможност да
                персонализираме рекламите, Вие ще продължавате да ги виждате, но те ще са много по-малко информативни и
                подходящи за Вас.{' '}
            </p>

            <p className='spd-text spd-text--accent'></p>

            <span className='spd-subtitle'>Какви са видовете бисквитки?</span>
            <p className='spd-text'>
                В зависимост от това колко време се съхраняват на вашето устройство, бисквитките се категоризират на
                „постоянни” и „временни” („сесийни“) бисквитки.
            </p>

            <ul className='spd-sublist'>
                <p>
                    • „Постоянните” бисквитки се съхраняват на Вашия компютър или мобилно устройство за дълъг период от
                    време, който може да бъде и години;
                </p>
                <p>
                    • „Временните” („сесийните“) бисквитки се поставят временно, когато посещавате нашите уебсайтове, но
                    същите не се съхраняват постоянно на вашето устройство и се заличават, когато Вашата сесия приключи,
                    например като затворите страницата.
                </p>
            </ul>

            <p className='spd-text'>
                В зависимост от функциите си, бисквитките най-общо може да се категоризират като четири различни
                типа:{' '}
            </p>
            <div className='spd-sublist'>
                <p>• &quot;стриктно необходими бисквитки&quot; („strictly necessary cookies”);</p>
                <p>• &quot;функционални бисквитки&quot; („functionality cookies”);</p>
                <p>• &quot;бисквитки за ефективност&quot; („performance cookies”);</p>
                <p>
                    • “бисквитки за поведенческо таргетиране или за рекламиране” („targeting or advertising cookies”).
                </p>
            </div>

            <div className='spd-sublist'>
                <div>
                    <span className={styles.subtitle}>
                        &quot;Стриктно необходими бисквитки&quot; („strictly necessary cookies”)
                    </span>
                    <p className='spd-text'>
                        Тези бисквитки са строго необходими, за да може уебсайтовете ни да изпълняват своите функции.
                    </p>
                    <p className='spd-text'>
                        Използваме ги например, за да установим нашите потребители, когато те използват нашите
                        уебсайтове, така че да можем да предоставим нашите услуги или за да можем да изпълняваме и да
                        поддържаме сигурността на нашите услуги. Тези бисквитки не събират информация за вас, която може
                        да бъде използвана за маркетинг или реклама. Съгласието Ви за използване на тези бисквитки се
                        предполага от самото използване на нашите сайтове, поради което не можете да откажете
                        използването им.{' '}
                    </p>
                </div>
                <div>
                    <span className={styles.subtitle}>
                        &quot;Функционални бисквитки&quot; („functionality cookies”)
                    </span>
                    <p className='spd-text'>
                        Чрез тези бисквитки се реализират различните функционалности на сайтовете ни, включително и
                        сервирането на неперсонализирана реклама. Те ни позволяват да запомним Вашите предпочитания,
                        като големината на шрифта и други ваши персонални настройки, спестявайки Ви време и усилия за
                        посочване на потребителско име всеки път, когато посетите страницата ни или за да видите
                        предпочитано от Вас съдържание.{' '}
                    </p>
                </div>
                <div>
                    <span className={styles.subtitle}>
                        &quot;Бисквитки за ефективност&quot; („performance cookies”)
                    </span>
                    <p className='spd-text'>
                        Тези бисквитки не са строго необходими, но ни позволяват да персонализираме Вашето преживяване
                        на уебсайтовете ни.
                    </p>
                    <p className='spd-text'>
                        Информацията, съхранена от &quot;бисквитките за ефективност&quot;, ни помага да разберем как
                        използвате уебсайтовете ни, да запомним и проверим историята на предишните Ви посещения, какво
                        сте гледали или сте маркирали или как сте ни открили.{' '}
                    </p>
                    <p className='spd-text'>
                        След това ще използваме тези данни, за да подобрим уебсайтовете и услугите ни, за по-добро Ваше
                        преживяване.{' '}
                    </p>
                    <p className='spd-text'>
                        Не използваме данните събрани от тези бисквитки за целите на сервиране на реклама или за целите
                        на персонализирането и.
                    </p>
                </div>
                <div>
                    <span className={styles.subtitle}>
                        “Бисквитки за поведенческо таргетиране или за рекламиране” („targeting or advertising cookies”).
                    </span>
                    <p className='spd-text'>
                        Нашата цел е да предоставяме на посетителите на нашия уебсайт информация, която е възможно
                        най-подходяща за тях. Това се постига не само чрез съдържанието на нашия сайт, но и чрез
                        показваните реклами. Тъй като трябва да продължим с излъчването на реклами, искаме те да бъдат
                        максимално полезни и подходящи за Вас.{' '}
                    </p>
                    <p className='spd-text'>
                        За да изпълним това, се опитваме да придобием представа за вероятните ВСи интереси на базата на
                        посещаваните от Вас уебсайтовет и поведението спрямо различното съдържание и реклами.
                        Впоследствие, на базата на тези интереси, ние адаптираме показваното съдържание и рекламите в
                        нашите уебсайтове за различните групи потребители.
                    </p>
                </div>
            </div>

            <span className={styles.subtitle}>Какви видове бисквитки използваме и за какви цели?</span>
            <p className='spd-text'>
                Когато посещавате нашите уебсайтове, вие можете да установите наличието на различни видове бисквитки,
                които се генерират. Ние използваме и четирите описани типа бисквитки.{' '}
            </p>
            <p className='spd-text'>Нашите уебсайтове използват бисквитки за следните цели:</p>
            <div className='spd-sublist'>
                <p>• Идентификация на потребители с цел осъществяване на безопасен достъп;</p>
                <p>• Запомняне на продуктите, добавени в кошницата Ви или закупени в процеса на онлайн пазаруването;</p>
                <p>
                    • Запомняне на информацията, попълнена от Вас в различни страници, когато плащате или правите
                    поръчки, за да не се налага да попълвате своите данни многократно;
                </p>
                <p>
                    • Предаване на информацията от една страница към следващата, например, при попълване на дълъг
                    въпросник или когато е необходимо да попълните много данни за да направите онлайн поръчка;
                </p>
                <p>
                    • Запазване на предпочитани настройки като например език, местоположение, брой резултати при
                    търсене, които да се визуализират и т.н.;
                </p>
                <p>
                    • Запазване на настройките за оптимална визуализация, като например размера на буфера и параметрите
                    за резолюцията на Вашия екран;
                </p>
                <p>
                    • Прочитане на настройките на Вашия браузър с цел оптимална визуализация на нашия уебсайт върху
                    екрана на Вашето устройство;
                </p>
                <p>• Безпроблемно зареждане на уебсайта и поддържане на постоянен достъп до него;</p>
                <p>
                    • Предлагане на опцията за запазване на данните за влизане във Вашия акаунт, така че да не се налага
                    да ги въвеждате всеки път;
                </p>
                <p>• Проследяване на броя на потребителите, посещаващи нашите уебстраници;</p>
                <p>• Проследяване на времето, прекарано от всеки посетител в разглеждане на нашите уебстраниците ни;</p>
                <p>
                    • Установяване на последователността, в която посетителите посещават различните страници в нашия
                    уебсайт;
                </p>
                <p>• Изготвяне на оценки за необходимостта от подобряване на някои части на нашите уебсайтове;</p>
                <p>• Оптимизация на уебсайтовете ни.</p>
            </div>
            <p className='spd-text'>
                Списък с бисквитките, които ние ползваме на нашите уебсайтове, може да намерите тук.
            </p>

            <span className={styles.subtitle}>Бисквитки на трети страни:</span>
            <p className='spd-text'>
                Използваме и някои бисквитки на трети страни като част от нашите услуги. Тези бисквитки се управляват от
                съответните трети страни и не се контролират от нас.{' '}
            </p>
            <p className='spd-text'>
                Тези бисквитки ни помагат за проучване на пазара, следене на приходите, подобряване на функционалността
                и наблюдение за спазване на нашите Общи условия и политиката за ползване на уебсайтовете ни.
            </p>
            <p className='spd-text'>
                Рекламодателите на нашите уебсайтове понякога използват свои бисквитки, за да Вви показват таргетирани
                реклами. Например, рекламодателите могат да използват профил базиран на сайтовете, които преди сте
                посещавали, за да Ви показват по-подходящи реклами, когато посещавате сайтове на Спортал.
            </p>

            <span className={styles.subtitle}>Как да управлявате бисквитките?</span>
            <p className='spd-text'>
                Вие можете да контролирате и/или да изтривате бисквитките, в зависимост от вашето желание.{' '}
            </p>
            <p className='spd-text'>
                Може да изтриете всички инсталирани на Вашия компютър бисквитки, както и да настроите повечето браузъри
                така, че да предотвратите разполагането им във Вашия компютър.{' '}
            </p>
            <p className='spd-text'>
                В случай, че направите това, може да е необходимо да правите промени в настройките на някои
                предпочитания всеки път, когато посещавате уебсайтовете ни, а някои от услугите и функциите на
                уебсайтовете ни е възможно да не работят.
            </p>
            <p className='spd-text'>
                Как да изтриете всички бисквитки от даден браузър (показана е информация за най-популярните браузъри):
            </p>
            <p>
                <a
                    className='spd-text__link'
                    href='https://support.google.com/chrome/answer/95647?co=GENIE.Platform%3DDesktop&amp;hl=bg'>
                    Chrome: https://support.google.com/chrome/answer/95647?co=GENIE.Platform%3DDesktop&amp;hl=bg
                </a>
            </p>
            <p>
                <a
                    className='spd-text__link'
                    href='https://support.mozilla.org/bg/kb/delete-cookies-remove-info-websites-stored'>
                    Firefox: https://support.mozilla.org/bg/kb/delete-cookies-remove-info-websites-stored{' '}
                </a>
            </p>
            <p>
                <a
                    className='spd-text__link'
                    href='https://support.microsoft.com/bg-bg/help/278835/how-to-delete-cookie-files-in-internet-explorer'>
                    Internet Explorer:
                    https://support.microsoft.com/bg-bg/help/278835/how-to-delete-cookie-files-in-internet-explorer
                </a>
            </p>
            <p>
                <a className='spd-text__link' href='https://support.apple.com/kb/ph21411?locale=bg_BG'>
                    Safari за компютър: https://support.apple.com/kb/ph21411?locale=bg_BG
                </a>
            </p>
            <p>
                <a className='spd-text__link' href='https://support.apple.com/bg-bg/HT201265'>
                    Safari за таблет и телефон: https://support.apple.com/bg-bg/HT201265
                </a>
            </p>
            <p className='spd-text'>
                Тук може да проверите какви бисквитки имате записани от най-използваните доставчици на персонализирани
                реклами и да управлявате Вашия избор:
                <a className='spd-text__link' href='https://optout.networkadvertising.org/?c=1#!/'>
                    https://optout.networkadvertising.org/?c=1#!/
                </a>
            </p>

            <span className={styles.subtitle}>Промени в нашата политика за бисквитки</span>
            <p className='spd-text'>
                Ние си запазваме правото да променяме настоящата Политиката за бисквитки и поради това препоръчваме да
                преглеждате периодично съдържанието и. Всички бъдещи промени в нашата Политика за бисквитките ще бъдат
                публикувани на тази страница, от който момент промените ще се считат за влезли в сила, освен ако не е
                упоменато друго.
            </p>
            <p className='spd-text'>Настоящата политика е актуална към дата 25.05.2018 г.</p>

            <span className={styles.subtitle}>Въпроси и обратна връзка</span>
            <p className='spd-text'>
                Моля, изпращайте вашите въпроси и коментари относно бисквитките или поверителността на адрес:
                gdpr@sportal.bg
            </p>
        </div>
    );
};

const PrivacyPolicyBody: FunctionComponent = () => {
    return (
        <div className='pt-3'>
            <h2 className={`${styles.title}`}>
                ПОЛИТИКА ЗА ПОВЕРИТЕЛНОСТ НА СПОРТАЛ ПРИ ОБРАБОТВАНЕ НА ВАШИТЕ ЛИЧНИ ДАННИ
            </h2>
            <span className={`${styles.subtitle}`}>Въведение</span>
            <p>
                С представяне на настоящите правила имаме за цел да Ви разясним каква информация събираме за Вас, защо и
                как го правим, какви са правата Ви съгласно Закона за защита на личните данни (ЗЗЛД) и Общия регламент
                за защита на личните данни (РЕГЛАМЕНТ (ЕС) 2016/679 НА ЕВРОПЕЙСКИЯ ПАРЛАМЕНТ И НА СЪВЕТА от 27 април
                2016 година - GDPR).
            </p>
            <span className={`${styles.subtitle}`}>Защо е необходимо Вашето съгласие?</span>
            <p>
                Наш изключителен приоритет е да защитаваме личните Ви данни и да получим Вашето свободно, информирано и
                недвусмислено съгласие за обработка на личните Ви данни от Спортал.{' '}
                <a href='https://corp.sportal.bg/bg/'>
                    <strong>Повече информация за Спортал и уебсайтовете на Спортал може да намерите тук.</strong>
                </a>
            </p>
            <p>
                Ако маркирате <strong>„Приемам всичко”</strong> Вие се съгласявате, че предоставяте личните си данни
                доброволно, че сте се запознали с правата си по ЗЗЛД и РЕГЛАМЕНТ (ЕС) 2016/679 НА ЕВРОПЕЙСКИЯ ПАРЛАМЕНТ
                И НА СЪВЕТА от 27 април 2016 година-GDPR и давате своето съгласие личните Ви данни да бъдат събирани,
                обработвани, съхранявани и предавани на трети лица за цели, които сме посочили по-долу.
            </p>

            <span className={`${styles.subtitle}`}>Определения</span>
            <p>
                „Лични данни“ означава всяка информация, свързана с идентифицирано физическо лице или физическо лице,
                което може да бъде идентифицирано („субект на данни“ или “потребител”); физическо лице, което може да
                бъде идентифицирано, е лице, което може да бъде идентифицирано, пряко или непряко, по-специално чрез
                идентификатор като име, идентификационен номер, данни за местонахождение, онлайн идентификатор или по
                един или повече признаци, специфични за физическата, физиологичната, генетичната, психическата и
                умствената, икономическата, културната или социалната идентичност на това физическо лице;
            </p>
            <p>
                „Администратор“ означава орган, който сам или съвместно с други органи определя целите и средствата за
                обработването на лични данни; когато целите и средствата за това обработване се определят от правото на
                Европейския съюз или правото на Република България, администраторът или специалните критерии за неговото
                определяне могат да бъдат установени в правото на Европейския съюз или в българското законодателство. В
                настоящия случай администраторът е „СПОРТАЛ.БГ” AД (наричано по-долу за краткост „Спортал”), дружество
                надлежно регистрирано и действащо съгласно законите на Република България с ЕИК 200818004, със седалище
                и адрес на управление: гр. София 1113, район Изгрев, ул. Николай Хайтов № 3А, партер, офис 1-5;
            </p>
            <p>
                „Обработващ лични данни“ означава физическо или юридическо лице, публичен орган, агенция или друга
                структура, която обработва лични данни от името на администратора;
            </p>
            <p>
                „Обработване“ означава всяка операция или съвкупност от операции, извършвана с лични данни или набор от
                лични данни чрез автоматични или други средства като събиране, записване, организиране, структуриране,
                съхранение, адаптиране или промяна, извличане, консултиране, употреба, разкриване чрез предаване,
                разпространяване или друга форма на осигуряване на достъп до данните, подреждане или комбиниране,
                ограничаване, изтриване или унищожаване;
            </p>
            <p>
                „Надзорен орган“ означава независим публичен орган от държава членка на Европейския съюз, отговорен за
                наблюдението на прилагането на правилата за защита на личните данни. За Република България надзорен
                орган е Комисията за защита на личните данни.
            </p>
            <span className={`${styles.subtitle}`}>Цел на политиката</span>
            <p>Тази политика за поверителност има за цел да гарантира, че Администраторът:</p>
            <p>
                спазва законодателството в областта на защитата на данните, а именно Закона за защита на личните данни
                (ЗЗЛД) и Общия регламент за защита на личните данни и останалото приложимо законодателство, и следва
                добрите практики;
            </p>
            <p>защитава потребителите, служителите, клиентите и партньорите на Спортал;</p>
            <p>гарантира прозрачност за начините, по които се съхраняват и обработват личните данни на субектите;</p>
            <p>осигурява защита от рисковете, свързани с нарушаване на данните;</p>
            <span className={`${styles.subtitle}`}>Как събираме информация за Вас?</span>
            <p>Ние можем да събираме информация за Вас по няколко начина:</p>
            <p>
                <i> Попълвани и изпращани от Вас форми, свързани с използваните услуги</i>
            </p>
            <p>Чрез изпращането им, Вие съзнателно споделяте лични данни.</p>
            <p>
                За всяка форма, съдържаща изпращане на лични данни, ще посочим целите на използване, срок на
                съхранението им, трети страни, с които евентуално ще ги споделим, начините да упражните правата си по
                Регламента.
            </p>
            <p>Ще минимизираме обема данни във формата спрямо според обявените цели.</p>
            <p>Например:</p>
            <p>
                когато създавате профил на уебсайтовете на Спортал, Вие ни предоставяте лична информация, включително
                имената си, телефонен номер и електронна поща.
            </p>
            <p>
                когато участвате в игри, конкурси или промоции на Спортал и/или на трети страни – Партньори на Спортал,
                Вие предоставяте лична информация според спецификата на играта, обичайно – имена, телефонен номер,
                електронна поща.
            </p>
            <p>
                когато закупувате артикули и стоки от нашия онлайн магазин за спортни стоки и екипировка, обичайно
                предоставяте лични данни – имена, телефонен номер, електронна поща, адрес за доставка, банкова сметка.
            </p>
            <p>
                <i>Данни, събирани чрез наблюдения</i>
            </p>
            <p>
                Понякога събираме информация за Вас наблюдавайки Вашето поведение на сайтовете ни и в мобилните ни
                апликации.
            </p>
            <p>
                За целта използваме стандартни за Internet подходи за проследяване на потребителското поведение, напр.
                използване на мрежово оборудване, за проследяване на IP адреса Ви, анализиране на информацията налична
                като част от комуникацията в изпозлваните от нас протоколи за запис и обмен на идентификатори на Вас и
                устройството Ви, напр. HTTP GET/POST заявките, използване функционалностите на JavaScript и други
                стандартни и широко разпространени в WEB програмни езици за извличане на данни и информация за контекста
                Ви браузър, операционна система, резолюция на екрана, език и др.
            </p>
            <p>
                <i>Данни, получени от трети страни</i>
            </p>
            <p>
                Понякога, след Ваше изрично съгласие дадено през инструментите партньора, можем да получим данни от
                трети страни, напр. данни от профила Ви в социални мрежи, като Facebook.
            </p>
            <p>Възможно е тези данни да бъдат обединени с други лични данни за Вас, с които вече разполагаме.</p>
            <p>
                Използвайки различни инструменти за онлайн анализ, ние можем да получим детайлни, но агрегирани данни за
                аудиторията на сайтовете ни. Това не са лични данни по смисъла на Регламента.
            </p>
            <p>
                <i>Бисквитки</i>
            </p>
            <p>
                Спортал използва на своите сайтове бисквитки за съхранение на настройки и идентификатори, необходими за
                предоставяне на посочните в сайта услуги. Някои от тези бисквитки са технически необходими за нормалното
                функциониране на сайтовете.
            </p>
            <p>Повече информация за политиката на Спортал по отношение на бисквитките може да намерите тук.</p>
            <span className={`${styles.subtitle}`}>
                Каква информация събираме и обработваме, когато ползвате нашите услуги?
            </span>
            <p>
                <i>Попълвани и изпращани от Вас форми</i>
            </p>
            <p>
                Възможно е да събираме разнообразни лични данни, според целите на формата и уговорките ни с рекламните
                партньори.
            </p>
            <p>Обичайно, събираме следните данни:</p>
            <p>Вашите имена;</p>
            <p>Потребителско име;</p>
            <p>Парола;</p>
            <p>Мобилен телефон;</p>
            <p>Адрес на електронна поща;</p>
            <p>Град;</p>
            <p>Адрес за физическа доставка – при електронна търговия;</p>
            <p>Данни за кореспонденция с Вас – при електронна търговия;</p>
            <p>Номер на банкова сметка – при електронна търговия.</p>
            <p>
                <i>Данни, събирани чрез наблюдения</i>
            </p>
            <p>
                За всички посетители ние и доставчиците на използваните от нас инструменти, обичайно събираме следните
                данни за Вас:
            </p>
            <p>IP адреса, от който правите посещението;</p>
            <p>Идентификатор на браузъра Ви;</p>
            <p>Идентификатор на мобилното Ви устройството;</p>
            <p>Несъдържащ лични данни Ваш идентификатор;</p>
            <p>История на поведението Ви – посетени страници, време прекарано на тях, закупени продукти и други;</p>
            <p>
                Поведение спрямо съдържанието на сайта/мобилната апликация – навигация, използвани интерфейсни елементи,
                време на престой;
            </p>
            <p>Вашите търсения;</p>
            <p>
                Поведение спрямо сервираните Ви реклами – какви и колко реклами са сервирани; къде са сервирани; видени
                реклами; реализирани кликове, пропуснати реклами и други;
            </p>
            <p>Вашите коментари.</p>
            <p>
                За логнатите потребители, към посочените по-горе данни, събираме и идентификатори, свързващи ги с
                конкретния профил на потребителя.
            </p>
            <p>
                <i>Данни, получени от трети страни</i>
            </p>
            <p>В зависимост от източника на данни, това включва:</p>
            <p>Основна информация за профила, като име, имейл адрес, дата на раждане;</p>
            <p>Подробности, които решите да споделите според конкретните настройки за социални медийни акаунти;</p>
            <p>Вашият контакти и начина Ви на свързаност с тях.</p>
            <span className={styles.subtitle}>За какви цели обработваме информацията?</span>
            <p className='spd-text'>
                Информацията, която събираме и обработваме за Вас може да се използва за следните цели:
            </p>
            <p className='spd-text'>
                <span>
                    <i>Нормално функциониране на сайтове ни</i>
                </span>
            </p>
            <p className='spd-text'>
                Тези данни са стриктно необходими за нормалната работа на сайтовете и мобилните ни апликации. Чрез тях
                можем да удостоверим самоличността Ви като логнат потребители, да Ви покажем по-добре съдържанието, да
                заредим сайта по-бързо, да защитаваме сайтовете и Вас от посегателства.
            </p>
            <p className='spd-text'>
                Такива данни например са данните от сесията Ви – информация дали сте логнат, кеширано съдържание и
                други.
            </p>
            <p className='spd-text'>
                Тук попадат и данните, свързани с идентифицирането Ви във връзка с регистрация за използване на наша
                услуга.
            </p>
            <p className='spd-text'>
                <span className='spd-text--italic'>
                    <i>За договорно изпълнение на услугите ни</i>
                </span>
            </p>
            <p className='spd-text'>
                Някои от услугите ни, като електронната търговия, изискват да споделите с нас лични данни. Използваме ги
                за да можем да изпълним договора си с Вас, за продажба и доставка на предлаганите от нас стоки.
            </p>
            <p className='spd-text'>
                Други предлагани от нас услуги са възможностите да добавяте новина, коментари, снимки или видеа.
                Използваме личните Ви данни, която ни дава възможност да Ви идентифицираме и при необходимост – да
                модерираме добавеното от Вас съдържание.
            </p>
            <p className='spd-text'>
                Тук попадат и данните, свързани с идентифицирането на потребителя във връзка с регистрация за използване
                на наша услуга.
            </p>
            <p className='spd-text'>
                <span className='spd-text--italic'>
                    <i>За запазване и достъп до информация</i>
                </span>
            </p>
            <p className='spd-text'>
                Тази цел е спомагателна за изпълнение на други цели – чрез използването на различни технически
                механизми, Спортал ще може да изпълни някоя от другите описани цели.
            </p>
            <p className='spd-text'>
                Включва съхранение и достъп до информация, запазена на Вашето устройство, напр. идентификатори на
                устройство, идентификатори на потребител, рекламни идентификатори, бисквитки и други технологии.
            </p>
            <p className='spd-text'>
                Тази цел е помощна – чрез използването на описаните в нея технически механизми, ние ще можем да изпълним
                някоя от другите описани цели.
            </p>
            <p className='spd-text'>
                <span className='spd-text--italic'>
                    <i>За персонализиране и профилиране</i>
                </span>
            </p>
            <p className='spd-text'>
                Събираме и обработваме информация за използването на услугите ни за последващо сервиране на
                персонализирана реклама и/или съдържание в друг контекст, напр. други сайтове и мобилни апликации, след
                време.
            </p>
            <p className='spd-text'>
                Типично, съдържанието и поведението Ви на сайта/мобилната апликации се използва за създаване на
                заключения за Вашите интереси, чрез които се управлява бъдещият избор на сервирани реклами и/или
                съдържание.
            </p>
            <p className='spd-text'>
                Целите, преследвани от Спортал са: осигуряване на по-добро преживяване на потребителите на сайтовете,
                мобилните апликации и услугите на Спортал, чрез показване на релевантни реклами.
            </p>
            <p className='spd-text'>
                <span className='spd-text--italic'>
                    <i>За подбор, доставка и отчитане на сервираните реклами</i>
                </span>
            </p>
            <p className='spd-text'>
                Спортал предлага безплатни за достъп от потребителите си уебсайтове и мобилни апликации. За да може да
                осигури финансиране на дейността си, Спортал сервира реклами на потребителите си.
            </p>
            <p className='spd-text'>
                Известно е, че сервирането на реклами и свързаните с тази дейност събиране на данни за потребителското
                поведение и изграждане на профили на потребителите са измежду най-сериозните причини за създаване на
                ОРЗД.
            </p>
            <p className='spd-text'>
                Спортал разбира отговорностите си към потребителите и се стреми да ограничи събирането и обработката на
                данни за целите на оптимизиране сервирането на реклами до възможния минимум. Все пак, технологичното
                развитие е довело до създаването на сложна система от отношения и обмен на данни, за тези цели.
            </p>
            <p className='spd-text'>
                Събираме информация и я комбинираме с предишно налична, за да подберем и Ви доставим релевантна реклама
                и за да можем да измерим доставката и ефективността й. Това може да включва предварително събрана
                информация за Вашите предишни интереси към подбрани реклами, обработка на данни за поведението Ви,
                когато рекламата Ви е била показана, колко често Ви се показват дадени реклами, къде и кога са Ви били
                показани, дали сте предприели действие свързано с рекламата, включително клик на рекламата и направена
                покупка.
            </p>
            <p className='spd-text'>Тази цел не включва персонализиране и профилиране.</p>
            <p className='spd-text'>
                <span className='spd-text--italic'>
                    <i>За подбор, доставка и отчитане на сервираното съдържание</i>
                </span>
            </p>
            <p className='spd-text'>
                Събираме информация и я комбинираме с предишно събрана, за да подберем и Ви доставим релевантно
                съдържание и да измерим доставката и ефективността му. Това може да включва предварително събрана
                информация за Вашите интереси към съдържанието, обработка на данни за показаното Ви съдържание, колко
                често или за колко време е било показано, дали сте предприели действие, свързано със съдържанието,
                включително клик на съдържанието.
            </p>
            <p className='spd-text'>Тази цел не включва персонализиране и профилиране.</p>
            <p className='spd-text'>
                <span className='spd-text--italic'>
                    {' '}
                    <i>За измерване</i>
                </span>
            </p>
            <p className='spd-text'>
                Събираме информация за Вашето използване на съдържанието и я комбинираме с предишно събрана, за да
                измерим, разберем и отчетем Вашето използване на услугите ни.
            </p>
            <p className='spd-text'>Тази цел не включва персонализиране и профилиране.</p>

            <p className='spd-text'>
                Нашият бизнес модел е изграден на основата на предоставяне на потребителите на безплатен достъп до
                съдържанието на уебсайтовете ни за сметка на сервиране на реклама. С оглед на нормалното функциониране
                на нашите уебсайтове и следване на нормалната бизнес логика, имаме нужда от базово измерване на
                потребителското Ви поведение.
            </p>

            <p className='spd-text'>
                За целта използваме единствено инструмента Google Analytics, като до момента на получаване на Вашето
                съгласие сме изключили функционалностите Google Анализ функции за рекламиране и сме подали команда за
                анонимизиране на Вашето IP към Google Analytics.
            </p>

            <p className='spd-text'>
                Ние смятаме, че по този начин, от една страна, сме минимизирали обема на събираната за целите на
                измерването информация до минимално необходимия за това размер и по този начин гарантираме, че тези
                данни не се използват за други цели, освен за Измерване, а от друга страна, вярваме, че защитаваме
                легитимния ни интерес за нормалното и необходимо функциониране на нашите услуги.
            </p>

            <p className='spd-text'>
                <span className='spd-text--italic'>
                    <i>По възложение и за проекти на трети страни</i>
                </span>
            </p>
            <p className='spd-text'>
                При конкретни проекти по възложение на трети страни събираме Ваши лични данни, които им споделяме за
                осъществяване целите на проектите им.
            </p>
            <p className='spd-text'>
                Детайлна информация за целите може да намерите в Общите условия на всеки конкретен проект, който ще Ви
                бъдат предоставени за получаване на Вашето съгласие.
            </p>
            <p className='spd-text'>
                <span className='spd-text--italic'>
                    <i>
                        За да Ви информираме за игри и промоционални оферти на Спортал и/или на трети страни – Партньори
                        на Спортал
                    </i>
                </span>
            </p>
            <p className='spd-text'>
                За изпълнение на тази цел, събираме Ваши лични данни, за да Ви информираме за наши или на трети страни,
                наши партньори, инициативи, промоции, игри, оферти и други.
            </p>
            <p className='spd-text'>За всички описани по-горе цели можем да използваме инструменти на трети страни.</p>
            <p className='spd-text'>
                <span className='spd-text--italic'>
                    <i>
                        За обработване на личните Ви данни, включително предоставяне на компетентни органи в
                        предвидените в законите случаи и за изпълнение на законови задължения на Спортал, както и за
                        цели, свързани с изпълнение на задълженията на страните по договор
                    </i>
                </span>
            </p>
            <p className='spd-text'>
                <span className='spd-text--italic'>За целите на планиране, отчетност и статистика</span>
            </p>
            <p className='spd-text'>
                За целите на планиране, отчетност и статистика означава всяка операция по събиране и обработване на
                лични данни, необходими за статистически изследвания или за изготвяне на статистически резултати, както
                и за подготовка, реализиране и проверка на планове и резултатите от тях.
            </p>
            <p className='spd-text'>
                <span className='spd-text--italic'>
                    <i>За Маркетингови цели</i>
                </span>
            </p>
            <p className='spd-text'>
                За да Ви изпращаме винаги актуална и полезна информация за нашите инициативи, събираме данни като адрес
                на електронна поща или друг начин за контакт с Вас – Viber, Skype, Messenger профил или такъв в друга
                комуникационна платформа.
            </p>

            <span className={styles.subtitle}>Кой друг има достъп до тази информация?</span>
            <p className='spd-text'>
                Ние понякога разкриваме вашата лична информация на трети лица - Партньори на Спортал, включително и на
                такива трети лица, находящи се извън Европейския съюз, при спазване на изискванията на Регламента, като
                например:
            </p>
            <div className='spd-sublist'>
                <p className='spd-text'>
                    Трети лица, с които Спортал има договорни правоотношения да организира игри, конкурси или промоции
                    за тези Партньори;
                </p>
                <p className='spd-text'>
                    Трети лица, с които Спортал има договорни правоотношения да публикува реклами и популяризира чрез
                    други подходящи способи техни продукти и услуги;
                </p>
                <p className='spd-text'>
                    Трети лица, с които Спортал има договорни правоотношения за извършване на
                    <span className='spd-text--italic'>
                        Измервания, За подбор, доставка и измерване на сервиране на съдържание или реклами
                    </span>
                    , включително сървъри за доставка на рекламно съдържани, борси за търгуване на инвентар (Ad
                    Exchanges) издателски инструменти (SSP), рекламодателски инструменти (DSP);
                </p>
                <p className='spd-text'>
                    Трети лица, с които Спортал има договорни правоотношения за ползване на колокационни услуги;
                </p>
                <p className='spd-text'>
                    Трети лица, с които Спортал има договорни правоотношения за ползване на Виртуални сървъри (VPS);
                </p>
                <p className='spd-text'>
                    Трети лица, предоставящи услуги за подобряване на рекламното таргетиране на продуктите и услугите на
                    Спортал и/или Партньорите на Спортал;
                </p>
                <p className='spd-text'>
                    Трети лица, предоставящи услуги във връзка със създаване на деперсонализирани потребителски
                    сегменти, според потребителското поведение;
                </p>
                <p className='spd-text'>
                    Индустриално признати доставчици на решения, публично декларирали съответствие с GDPR и e-privacy
                    политиките, като Google и Facebook. Съгласието включва и поставяне и използване на маркиращи
                    уникални деперсонализирани идентификатори, включително и с използване на cookies, localStorage или
                    друга подходяща общоприета web технология;
                </p>
                <p className='spd-text'>
                    Трети лица, предоставящи услуги на Спортал като Счетоводители и счетоводни предприятия; Одитори и
                    Одиторски предприятия; Юристи – Адвокати, адвокатски кантори и юрисконсулти; Нотариуси; Рекламни и
                    ПР агенции; Дружества извършващи дейност по системно администриране на данни.
                </p>
            </div>

            <span className={styles.subtitle}>Какви са правата Ви във връзка с личните данни?</span>
            <p className='spd-text'>
                Наш приоритет е както да Ви информираме за Вашите законови права, така и да гарантираме тяхното
                спазване. Вие имате следните права, които следва да се съблюдават при обработване на личните Ви данни:
            </p>
            <div className='spd-sublist'>
                <p className='spd-text'>
                    Право във всеки един момент да оттеглите съгласието си за обработка на личните Ви данни;
                </p>
                <p className='spd-text'>
                    Да поискате коригиране или допълване на данните Ви при констатирана неточност чрез писмено
                    заявление, отправено до нас;
                </p>
                <p className='spd-text'>Право на достъп до личните Ви данни;</p>
                <p className='spd-text'>Право на ограничаване обработването на личните Ви данни;</p>
                <p className='spd-text'>Право на възражение срещу обработването на личните Ви данни;</p>
                <p className='spd-text'>Правото на преносимост на данните;</p>
                <p className='spd-text'>
                    Право да поискате изтриване на личните Ви данни, които се обработват незаконосъобразно или с
                    отпаднало правно основание (оттеглено съгласие, изпълнена първоначална цел, за която са били събрани
                    и др.);
                </p>
                <p className='spd-text'>
                    Право да поискате от Администратора на личните Ви данни да уведомите третите лица, на които
                    Администраторът е предоставил Вашите данни, по отношение на коригиране, изтриване или ограничаване
                    на обработването на личните данни.
                </p>
                <p className='spd-text'>Имате право на жалба до Комисия за защита на личните данни.</p>
            </div>

            <span className={styles.subtitle}>Кога Спортал може да ползва данните Ви, за да се свърже с Вас?</span>
            <p className='spd-text'>
                Можем да използваме предоставената от Вас информация, за да се свържем с Вас относно различни въпроси
                като:
            </p>
            <div className='spd-sublist'>
                <p>да Ви уведомим за промени в политиките на Спортал и настоящите правила;</p>
                <p>да Ви отговорим, когато сте се свързали с нас или да Ви отговорим на Ваш коментар или жалба;</p>
                <p>да Ви поканим да участвате в наши проучвания по отношение на услугите на Спортал;</p>
                <p>за маркетингови цели;</p>
                <p>
                    за да Ви изпратим подходящи предложения за продукти и услуги на Спортал и/или на трети страни –
                    Партньори на Спортал;
                </p>
                <p>
                    за да Ви информираме за игри, конкурси или промоционални оферти на Спортал и/или на трети страни –
                    Партньори на Спортал;
                </p>
            </div>
            <p className='spd-text'>Никога няма да се свържем с Вас, за да поискаме паролата от Вашия акаунт.</p>

            <span className={styles.subtitle}>Как мога да получа, актуализирам и управлявам моите лични данни?</span>
            <p className='spd-text'>
                Вие можете лесно да актуализирате и управлявате Вашата информация, като отидете на
                <a href='#' className='spd-text__link'>
                    „Настройки за поверителност”.
                </a>
            </p>
            <p className='spd-text'>
                Ако маркирате
                <a href='#' className='spd-text__link'>
                    „Приемам всичко”
                </a>{' '}
                Вие се съгласявате с горепосочената информация, като може да промените или да се откажете от съгласията
                си, като отидете на
                <a href='#privacy' className='spd-text__link'>
                    „Настройки за поверителност”.
                </a>
            </p>
            <p className='spd-text'>
                За да може по-лесно да управлявате съгласията си, сме изградили Център за поверителност, който включва и
                инструмент за управление на Вашата поверителност (privacy dashboard).
            </p>
            <p className='spd-text'>
                Чрез него, Вие можете да разгледате и управлявате дадените съгласия и да заявите изпълнения на
                предвидените в GDPR права.
            </p>
            <p className='spd-text'>
                Нашите уебсайтове използват „бисквитки”. Ако искате да научите повече за използването за „бисквитките”
                на нашите уебсайтове и това как да ги управлявате, прочетете нашата
                <a href='#cookies' className='spd-text__link'>
                    „Политика за Бисквитки”
                </a>
                .
            </p>

            <span className={styles.subtitle}>Заключение</span>
            <p className='spd-text'>Настоящата политика за поверителност е актуална към дата 25.05.2018 г.</p>
            <p className='spd-text'>
                Спортал запазва правото си по всяко време да променя и/или допълва настоящата Политика. Промените влизат
                в сила веднага след публикуването им на уебсайта, освен ако в актуализираната версия на Политиката не е
                предвидено друго. Трябва периодично да посещавате тази страница за преглед на Политиката. Политиката
                може да бъде актуализирани по всяко време без специално уведомяване на потребителите на Сайта. Спортал
                не носи отговорност, ако даден потребител на уебсайтовете не се е запознал с последния актуален вариант
                на настоящата Политиката.
            </p>

            <p className='spd-text'>
                Моля, изпращайте вашите въпроси и коментари относно бисквитките или поверителността на адрес:
                gdpr@sportal.bg
            </p>
        </div>
    );
};
